import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import CloseIcon from '../../assets/icons/close-dialog.svg';
import LockIcon from '../../assets/icons/lock.svg';
import StarIcon from '../../assets/icons/star.svg';
import BlueCircle from '../../assets/images/blue_circle.svg';
import TUEV from '../../assets/images/TUEV.png';
import Button from '../../atoms/Button';
import ErrorMessage from '../../atoms/ErrorMessage/ErrorMessage';
import AmountField from '../../molecules/AmountField/AmountField';
import SelectField from '../../molecules/SelectField';
import TabField, { DIRECTION_VERTICAL } from '../../molecules/TabField/TabField';
import deepDefaultProps from '../../util/deepDefaultProps';
import {
  buildRequestData,
  buildTargetUrl,
  generateNumberBasedSelectOptionsRange,
  getOfferByBestRate,
  getProductOfferByBestRate,
  getPurposeOptions,
  mapPurposeToCafePurpose,
  relationOptions,
} from '../../util/formHelper';
import { MOCK_CAFE_INFO_OBLIGATION_URL, MOCK_CAFE_PRIVACY_URL, MOCK_CAFE_TERMS_URL } from '../../util/mockData';
import {
  BankLogo,
  CenteredText,
  ContactFieldset,
  CreditFieldset,
  Ekomi,
  EkomiImage,
  EkomiRating,
  EkomiStars,
  Footer,
  Form,
  HelpBox,
  HelpBoxClose,
  HelpButton,
  InputContainer,
  MaxWidthRow,
  Section,
  TrustElement,
  TrustElements,
  TrustIcon,
  TrustQuote,
  TrustText,
  TrustTitle,
} from './BankForm.style';
import BankFormFooter from './BankFormFooter';


// eslint-disable-next-line max-len
const ERROR_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'; // White Transparent Pixel formatted in base64
const onImageError = (event) => {
  if (event && event.target) {
    event.target.src = ERROR_IMAGE;
  }
};

const debtorOptions = [
  {
    value: '1',
    label: '1 Kreditnehmer',
  },
  {
    value: '2',
    label: '2 Kreditnehmer',
  },
];

/**
 * Prefix for all keys within the WebStorage
 */
export const STORAGE_KEY_PREFIX = 'fc-lead-';
export const STORAGE_KEY_RESUME_HASH = STORAGE_KEY_PREFIX + 'resumeHash';

class BankForm extends PureComponent {

  static propTypes = {
    affiliateData: PropTypes.object.isRequired,
    submitUrl: PropTypes.instanceOf(URL).isRequired,
    resolveToUrl: PropTypes.func.isRequired,
    resolveWithLoader: PropTypes.func.isRequired,
  };

  /**
   * @constructor
   * @param {Object} props
   */
  constructor(props) {
    super(props);

    const {
      affiliateData: {
        advertisementId,
        amount,
        term,
        purpose,
        debtors,
        relation,
      },
    } = this.props;

    /** @type {URL} */
    this.privacyUrl = new URL(
      process.env.REACT_APP_CAFE_PRIVACY_URL || MOCK_CAFE_PRIVACY_URL,
    );
    /** @type {URL} */
    this.termsUrl = new URL(
      process.env.REACT_APP_CAFE_TERMS_URL || MOCK_CAFE_TERMS_URL,
    );
    /** @type {URL} */
    this.infoObligationUrl = new URL(
      process.env.REACT_APP_CAFE_INFO_OBLIGATION_URL ||
      MOCK_CAFE_INFO_OBLIGATION_URL,
    );

    this.privacyUrl.searchParams.set('configuration', advertisementId);
    this.termsUrl.searchParams.set('configuration', advertisementId);
    this.infoObligationUrl.searchParams.set('configuration', advertisementId);

    /** @type {HTMLFormElement} */
    this.formElement = React.createRef();

    this.state = {
      amount,
      term,
      purpose,
      debtors,
      relation,
      bestOffer: {},
      showError: false, // TODO rename to hasError since this form may HAVE an error
      showHelp: false,
      amountMin: 1000,
      amountMax: 100000,
      termOptions: generateNumberBasedSelectOptionsRange(12, 84),
      purposeOptions: getPurposeOptions(),
    };

    this.updateBestOffer().then(() => this.updateBankLogo());
  }

  componentDidUpdate = (prevProps, prevState) => {
    const { amount, term, purpose } = prevState;

    if (
      this.state.amount !== amount ||
      this.state.term !== term ||
      this.state.purpose !== purpose
    ) {
      this.updateBestOffer();
    }
  };

  /**
   * Will update the best offer display
   */
  updateBestOffer = async () => {
    const {
      props: {
        affiliateData: { advertisementId, productId },
        resolveWithLoader,
      },
      state: { amount, term, purpose },
    } = this;

    const params = {
      amount,
      term,
      purpose,
    };

    const offerPromise = productId
      ? getProductOfferByBestRate(productId, params, advertisementId)
      : getOfferByBestRate(params, advertisementId);

    const bestOffer = await resolveWithLoader(offerPromise);
    if (!bestOffer) {
      this.setState({ bestOffer: { invalid: true } });
      return;
    }

    // store offer parameters on the offer
    bestOffer.amount = amount;
    bestOffer.term = term;
    bestOffer.purpose = purpose;

    this.setState({
      bestOffer,
    }, this.updateBankLogo);
  };

  updateBankLogo = async () => {
    const {
      affiliateData: { noLogo },
    } = this.props;
    if (noLogo && noLogo !== 'false') {
      return;
    }
    const { bestOffer } = this.state;
    if (bestOffer) {
      this.setState({
        bankLogoUrl: `https://cdn.finanzcheck.de/products/widgets/${bestOffer.bank.code}.svg`,
      });
    }
  };

  /**
   * Handles change event on amount input
   * Verifies new value and saves it into state
   *
   * @param {Number} value
   */
  handleAmountChange = (event, value) => {
    const { amountMax, amountMin } = this.state;

    if (value >= amountMin && value <= amountMax) {
      this.setState({
        amount: value,
      });
    }
  };

  /**
   * Handles change event on term input
   * Verifies new value and saves it into state
   *
   * @param {Number} value
   */
  handleTermChange = (event, value) => {
    const { termOptions } = this.state;
    const options = termOptions.filter(item => item.value === value);

    if (options.length) {
      this.setState({
        term: value,
      });
    }
  };

  /**
   * Handles change event on purpose input
   * Verifies new value and saves it into state
   *
   * @param {Number} value
   */
  handlePurposeChange = (event, value) => {
    const { purposeOptions } = this.state;
    const options = purposeOptions.filter(item => item.value === value);

    if (options.length) {
      this.setState({
        purpose: value,
      });
    }
  };

  handleDebtorChange = (event, value) => {
    const options = debtorOptions.filter(item => item.value === value);

    if (options.length) {
      this.setState({
        debtors: value,
      });
    }
  };

  /**
   * TODO rename to "handleChange"
   * Handles change event on form
   * Checks validity and updates state
   */
  handleValidity = () => {
    if (this.state.showError && this.formElement.current.checkValidity()) {
      this.setState({
        showError: false,
      });
    }
  };

  /**
   * Handles invalid event on form
   */
  handleInvalid = () => {
    this.setState({
      showError: ' Bitte überprüfen Sie ihre Eingaben!',
    });
  };

  handleHelpToggle = () => {
    const { showHelp } = this.state;
    this.setState({
      showHelp: !showHelp,
    });
  };

  /**
   * Handles submit event on form
   *
   * @param {Event} event
   */
  handleSubmit = (event) => {
    event.preventDefault();

    const form = event.target;

    if (!form.checkValidity()) return;
    const {
      props: {
        affiliateData: {
          advertisementId,
          subId,
          targetUrl,
          purpose,
          parameters,
          submitMode,
        },
      },
      state: {
        amount,
        term,
      },
    } = this;

    if (targetUrl !== undefined) {
      const url = buildTargetUrl(targetUrl, {
        ...parameters,
        advertisementId,
        amount,
        purpose: mapPurposeToCafePurpose(purpose),
        subId,
        term,
      });
      switch (submitMode) {
        default:
        case 'REDIRECT':
          window.location.href = url;
          break;
        case 'WINDOW':
          window.open(url, '_blank');
          break;
        case 'AMP':
          window.open(url, '_top');
          break;
      }
      return;
    }


    const formData = new FormData(form);
    const resumeHash = sessionStorage.getItem(STORAGE_KEY_RESUME_HASH);

    if (resumeHash) {
      formData.append('resumeHash', resumeHash);
    }

    const requestData = buildRequestData(formData, this.props.affiliateData);
    const request = new Request(this.formElement.current.action, {
      method: 'POST',
      headers: new Headers({
        Accept: 'application/json',
      }),
      body: JSON.stringify(requestData),
    });

    const promise = fetch(request)
      .then((response) => {
        if (!response.ok) {
          this.setState({
            showError:
              'Der Server meldete einen Fehler. Bitte überprüfen Sie Ihre Daten noch einmal!',
          });
          throw new Error(
            `Server responded with status ${response.status} - ${response.statusText}`,
          );
        }

        if (
          !response.headers.get('Content-Type').match(/^application\/json\b/)
        ) {
          throw new Error(
            'Bad content type: ' + response.headers.get('Content-Type'),
          );
        }

        return response.json().then((data) => {
          sessionStorage.setItem(
            STORAGE_KEY_RESUME_HASH,
            data.result.resumeHash,
          );
          data.request = requestData; // extend data object for callback
          this.setState({ showError: false });
          return data;
        });
      })
      .catch((reason) => {
        if (!this.state.showError) {
          this.setState({
            showError:
              'Beim Übertragen der Daten ist ein Fehler aufgetreten. Bitte versuchen Sie es später noch einmal!',
          });
        }
        console.error('API request failed: ' + reason);
      });

    // resolve promise with loading indicator and open the resulting cafe url
    const { resolveToUrl, resolveWithLoader } = this.props;
    resolveToUrl(resolveWithLoader(promise));
  };

  /**
   * @returns {JSX}
   */
  render() {
    const {
      affiliateData: { buttonText, hero },
    } = this.props;

    const {
      amountMin,
      amountMax,
      amount,
      term,
      termOptions,
      purpose,
      purposeOptions,
      debtors,
      relation,
      bestOffer,
      showError,
      showHelp,
      bankLogoUrl,
    } = this.state;

    return (
      <Form
        ref={this.formElement}
        action={this.props.submitUrl}
        method="post"
        onChange={this.handleValidity}
        onInvalid={this.handleInvalid}
        onSubmit={this.handleSubmit}
      >
        <CreditFieldset>
          {bankLogoUrl && (
            <BankLogo>
              <img
                src={bankLogoUrl}
                alt="Bank Logo"
                onError={onImageError}
              />
            </BankLogo>
          )}
        </CreditFieldset>
        <InputContainer>
          <CreditFieldset>
            <Section>
              <div>Ihre Kreditanfrage</div>
            </Section>
            <MaxWidthRow>
              <AmountField
                name="amount"
                label="Nettodarlehensbetrag"
                onChange={this.handleAmountChange}
                placeholder="10.000"
                value={amount}
                min={amountMin}
                max={amountMax}
                unit="Euro"
                required
                autoComplete="off"
              />
            </MaxWidthRow>
            <MaxWidthRow>
              <SelectField
                name="term"
                label="Laufzeit"
                onChange={this.handleTermChange}
                value={term.toString()}
                options={termOptions}
              />
            </MaxWidthRow>
            <MaxWidthRow>
              <SelectField
                name="purpose"
                label="Verwendungszweck"
                onChange={this.handlePurposeChange}
                value={purpose}
                options={purposeOptions}
                required
              />
            </MaxWidthRow>
          </CreditFieldset>

          <ContactFieldset>
            {!showHelp && (
              <HelpButton onClick={this.handleHelpToggle}>
                <BlueCircle />
                <span>?</span>
              </HelpButton>
            )}
            <Section>Anzahl der Kreditnehmer</Section>
            {showHelp && (
              <HelpBox>
                <HelpBoxClose onClick={this.handleHelpToggle}>
                  <CloseIcon />
                </HelpBoxClose>
                Mit einem zweiten Kreditnehmer erhöhen Sie die
                Wahrscheinlichkeit, für Ihren Kredit den günstigsten Zins zu
                erhalten.
              </HelpBox>
            )}
            <MaxWidthRow>
              <TabField
                name="debtors"
                onChange={this.handleDebtorChange}
                direction={DIRECTION_VERTICAL}
                options={debtorOptions}
                required
                value={debtors}
              />
            </MaxWidthRow>
          </ContactFieldset>

          {debtors === '2' && (
            <ContactFieldset>
              <Section>Verhältnis der Kreditnehmer</Section>
              <MaxWidthRow>
                <TabField
                  name="relation"
                  direction={DIRECTION_VERTICAL}
                  options={relationOptions}
                  required
                  value={relation}
                />
              </MaxWidthRow>
            </ContactFieldset>
          )}
        </InputContainer>

        <ContactFieldset>
          <MaxWidthRow>
            <Button
              type="submit"
              label={buttonText}
              disabled={bestOffer.invalid}
            />
          </MaxWidthRow>
        </ContactFieldset>

        <ContactFieldset>
          <CenteredText>
            <LockIcon /> Sichere SSL-Verschlüsselung
          </CenteredText>
        </ContactFieldset>

        <Footer>
          {showError && (
            <ErrorMessage>
              <p>
                <strong>Achtung: </strong>
                {showError}
              </p>
            </ErrorMessage>
          )}

          {bestOffer.invalid && (
            <ErrorMessage>
              <p>
                <strong>Achtung: </strong>
                Unter diesen Konditionen können wir ihnen leider kein Angebot
                machen.
              </p>
            </ErrorMessage>
          )}
        </Footer>

        {!hero && (
          <TrustElements>
            <TrustElement>
              <TrustTitle>TÜV geprüftes Onlineportal</TrustTitle>
              <a target="_blank" rel="noopener noreferrer" href="https://www.tuev-saar.de/zertifikat/tk45089/">
                <TrustIcon>
                  <img
                    style={{
                      height: 44,
                    }}
                    src={TUEV}
                    alt="TÜV Saarland: Geprüftes-Portal"
                    title="TÜV Saarland Siegel: Geprüftes Online-Portal"
                    className="Seal-image"
                  />
                </TrustIcon>
              </a>
              <TrustText>
                Einfache Bedienung, sichere Datenübertragung, qualifizierter
                Kundenservice
              </TrustText>
            </TrustElement>

            <TrustElement>
              <TrustTitle>Höchste Kundenzufriedenheit</TrustTitle>
              <TrustIcon onClick={() => {
                window.open('https://www.ekomi.de/bewertungen-finanzcheck.html', '_blank', 'noopener');
              }}
              >
                <Ekomi>
                  <EkomiImage
                    src="https://www.finanzcheck.de/_next/static/images/ekomi-stamp-min.png"
                    srcSet="https://www.finanzcheck.de/_next/static/images/ekomi-stamp-min.png 1x,
                    https://www.finanzcheck.de/_next/static/images/ekomi-stamp@2x-min.png 2x"
                    alt="Kundenauszeichnung eKomi"
                    height="44"
                  />
                  <EkomiRating>
                    <div>FINANZCHECK</div>
                    <div title="4,9 von 5 Sternen">
                      <EkomiStars>
                        <StarIcon />
                        <StarIcon />
                        <StarIcon />
                        <StarIcon />
                        <StarIcon />
                      </EkomiStars>
                    </div>
                    <div>4,9 / 5</div>
                  </EkomiRating>
                </Ekomi>
              </TrustIcon>
              <TrustQuote>
                „Sensationelle Beratung. Ich bin sehr zufrieden mit dem Service
                und kann es nur weiterempfehlen!”
              </TrustQuote>
            </TrustElement>
          </TrustElements>
        )}

        {!hero && (
          <BankFormFooter />
        )}
      </Form>
    );
  }

}

export default deepDefaultProps({
  affiliateData: {
    buttonText: 'Weiter',
    amount: 10000,
    term: 84,
    purpose: 'OTHER',
    debtors: '',
    relation: '',
    productId: '',
  },
})(BankForm);
